import { DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { scheduledChannelSnapshotFrequencyTranslationMap } from 'dashboard/constants/scheduled-channel-snapshot-frequency-translation-map.constant';
import { ScheduledChannelSnapshotFrequency } from 'dashboard/enums/scheduled-channel-snapshot-frequency.enum';
import { ScheduledChannelSnapshotListingsSelectionMode } from 'dashboard/enums/scheduled-channel-snapshot-selection-mode.enum';
import { ScheduledChannelSnapshot } from 'dashboard/interfaces/scheduled-channel-snapshot.interface';
import { ScheduledChannelSnapshotsService } from 'dashboard/services/scheduled-channel-snapshots.service';
import { updateTreeValidity } from 'dynamic-form/util/update-tree-validity';
import { NumberValidator } from 'dynamic-form/validators/number-validator';
import { ImportChannel } from 'exports/interfaces/imports-runner.interface';
import { Listing } from 'listings/interfaces/listing.interface';
import { ListingsService } from 'listings/services/listings.service';
import { filter } from 'rxjs';
import { Supplier } from 'settings/interfaces/supplier.interface';
import { ChannelsService } from 'settings/services/channels.service';
import { SuppliersService } from 'settings/services/suppliers.service';
import { BaseModal } from 'shared/abstract/base-modal';
import { DataGridComponent } from 'shared/components/data-grid/data-grid.component';
import { DefaultDataGridActionCellRendererComponent } from 'shared/components/data-grid/default-data-grid-action-cell-renderer/default-data-grid-action-cell-renderer.component';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { MultiSelectInputComponent } from 'shared/components/multi-select-control/multi-select-input.component';
import { OptionSwitchComponent } from 'shared/components/option-switch/option-switch.component';
import { TypeaheadSelectComponent } from 'shared/components/typeahead-select/typeahead-select.component';
import { BlurOnEnterDirective } from 'shared/directives/blur-on-enter.directive';
import { IfNotDisabledDirective } from 'shared/directives/if-not-disabled.directive';
import { AuditableEntity } from 'shared/interfaces/auditable-entity.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { DataGridColumnDefinition } from 'shared/interfaces/data-grid.interface';
import { FormDifferService, ModalService, ToastService, UserApiService } from 'shared/services';
import { SharedModule } from 'shared/shared.module';
import { formGroupCheckboxesSelectionRequired } from 'shared/validators/form-group-checkboxes-selection-req';

type FieldStatus = 'disable' | 'enable';

const frequencyFormFieldStatusMap: {
    [key in ScheduledChannelSnapshotFrequency]: {
        daysOfWeek: FieldStatus;
        time: FieldStatus;
        dayOfMonth: FieldStatus;
    };
} = {
    [ScheduledChannelSnapshotFrequency.DAILY]: { daysOfWeek: 'disable', time: 'enable', dayOfMonth: 'disable' },
    [ScheduledChannelSnapshotFrequency.WEEKLY]: { daysOfWeek: 'enable', time: 'enable', dayOfMonth: 'disable' },
    [ScheduledChannelSnapshotFrequency.MONTHLY]: { daysOfWeek: 'disable', time: 'enable', dayOfMonth: 'enable' },
    [ScheduledChannelSnapshotFrequency.MANUAL]: { daysOfWeek: 'disable', time: 'disable', dayOfMonth: 'disable' },
};

@Component({
    selector: 'app-scheduled-channel-snapshot-modal',
    templateUrl: './scheduled-channel-snapshot-modal.component.html',
    styleUrls: ['./scheduled-channel-snapshot-modal.component.scss'],
    imports: [
        SharedModule,
        TranslateModule,
        MultiSelectInputComponent,
        IfNotDisabledDirective,
        DataGridComponent,
        FormErrorComponent,
        TypeaheadSelectComponent,
        OptionSwitchComponent,
        DatePipe,
        BlurOnEnterDirective,
    ],
})
export class ScheduledChannelSnapshotModalComponent extends BaseModal implements OnInit {
    public data: any;

    public currentUserId = signal<string>('');

    public currentSnapshotEntityTracking = signal<AuditableEntity | undefined>(undefined);

    public isShijiUser = signal<boolean>(false);

    public userCompanyName = signal<string>('');

    private pendingSelectedListingIds: string[] | null = null;

    public form: FormGroup<{
        supplierId: FormControl<string>;
        enabled: FormControl<boolean>;
        distributionChannels: UntypedFormControl;
        schedule: FormGroup<{
            frequency: FormControl<ScheduledChannelSnapshotFrequency>;
            time: FormControl<string>;
            daysOfWeek: FormGroup<{
                monday: FormControl<boolean>;
                tuesday: FormControl<boolean>;
                wednesday: FormControl<boolean>;
                thursday: FormControl<boolean>;
                friday: FormControl<boolean>;
                saturday: FormControl<boolean>;
                sunday: FormControl<boolean>;
            }>;
            dayOfMonth: FormControl<number | null>;
        }>;
        tempListing: FormControl<Listing | null>;
        listingSelectionOptionTypeValue: FormControl<ScheduledChannelSnapshotListingsSelectionMode>;
        selectedListings: FormArray<FormControl<string>>;
    }> = new FormGroup({
        supplierId: new FormControl('', { nonNullable: true, validators: Validators.required }),
        enabled: new FormControl(true, { nonNullable: true, validators: Validators.required }),
        distributionChannels: new UntypedFormControl('', [Validators.required]),
        schedule: new FormGroup({
            frequency: new FormControl<ScheduledChannelSnapshotFrequency>(ScheduledChannelSnapshotFrequency.DAILY, {
                nonNullable: true,
                validators: Validators.required,
            }),
            time: new FormControl('00:00', { nonNullable: true, validators: Validators.required }),
            daysOfWeek: new FormGroup(
                {
                    monday: new FormControl(false, { nonNullable: true }),
                    tuesday: new FormControl(false, { nonNullable: true }),
                    wednesday: new FormControl(false, { nonNullable: true }),
                    thursday: new FormControl(false, { nonNullable: true }),
                    friday: new FormControl(false, { nonNullable: true }),
                    saturday: new FormControl(false, { nonNullable: true }),
                    sunday: new FormControl(false, { nonNullable: true }),
                },
                [formGroupCheckboxesSelectionRequired],
            ),
            dayOfMonth: new FormControl<number | null>(
                {
                    value: null,
                    disabled: true,
                },
                {
                    validators: [Validators.required, NumberValidator({ min: 1, max: 31, step: 1 })],
                },
            ),
        }),
        tempListing: new FormControl<Listing | null>(null, { nonNullable: false }),
        listingSelectionOptionTypeValue: new FormControl<ScheduledChannelSnapshotListingsSelectionMode>(
            ScheduledChannelSnapshotListingsSelectionMode.INCLUDE,
            { nonNullable: true },
        ),
        selectedListings: new FormArray<FormControl<string>>([]),
    });

    public readonly ScheduledChannelSnapshotFrequency = ScheduledChannelSnapshotFrequency;
    public readonly ScheduledChannelSnapshotListingSelectionMode = ScheduledChannelSnapshotListingsSelectionMode;
    public readonly scheduledChannelSnapshotFrequencyTranslationMap = scheduledChannelSnapshotFrequencyTranslationMap;

    public suppliers: Supplier[] = [];

    get distributionChannels(): ImportChannel[] {
        return this.channelsService.importsEligibleChannels();
    }

    supplierIdValue = toSignal(this.form.get('supplierId')!.valueChanges, { initialValue: this.form.get('supplierId')!.value });

    listingsSelectionMode = toSignal(this.form.get('listingSelectionOptionTypeValue')!.valueChanges, {
        initialValue: this.form.get('listingSelectionOptionTypeValue')!.value,
    });

    public listings = signal<Listing[]>([]);

    public listingsPlaceholder = computed(() => {
        const selectedListingsIds = this.selectedListingsIdsValue();
        const hasItems = selectedListingsIds.length > 0;
        const mode = this.listingsSelectionMode();

        if (mode === ScheduledChannelSnapshotListingsSelectionMode.EXCLUDE) {
            return hasItems
                ? 'dashboard.scheduledChannelSnapshots.selectionMode.excludeSpecific'
                : 'dashboard.scheduledChannelSnapshots.selectionMode.excludeNone';
        } else {
            return hasItems
                ? 'dashboard.scheduledChannelSnapshots.selectionMode.includeSpecific'
                : 'dashboard.scheduledChannelSnapshots.selectionMode.includeAll';
        }
    });

    private listingsFirstItem = computed<Listing>(() => {
        const mode = this.listingsSelectionMode();

        let label: string;
        if (mode === ScheduledChannelSnapshotListingsSelectionMode.INCLUDE) {
            label = 'dashboard.scheduledChannelSnapshots.selectionMode.includeAll';
        } else {
            label = 'dashboard.scheduledChannelSnapshots.selectionMode.excludeNone';
        }

        return {
            id: '-1',
            name: label,
        } as Listing;
    });

    public listingOptions = computed(() => {
        const filtered = this.listings().filter(
            (listing) => !this.selectedListingsIdsValue().find((addedListing) => addedListing === listing.id),
        );

        return [this.listingsFirstItem(), ...filtered];
    });

    frequencyValue = toSignal(this.form.get('schedule.frequency')!.valueChanges, {
        initialValue: this.form.get('schedule.frequency')!.value,
    });

    selectedListingsIdsValue = toSignal(this.form.get('selectedListings')!.valueChanges, {
        initialValue: this.form.get('selectedListings')!.value,
    });

    public selectedListings = computed(() => {
        return this.listings().filter((listing) => this.selectedListingsIdsValue().find((addedListing) => addedListing === listing.id));
    });

    get selectedListingsIds(): FormArray<FormControl<string>> {
        return this.form.get('selectedListings') as FormArray<FormControl<string>>;
    }

    importItemColumns: DataGridColumnDefinition[] = this.generateExcludedListingsTableColumns();

    constructor(
        private toastService: ToastService,
        private destroyRef: DestroyRef,
        private suppliersService: SuppliersService,
        private channelsService: ChannelsService,
        private scheduledChannelSnapshotsService: ScheduledChannelSnapshotsService,
        private listingsService: ListingsService,
        private userApiService: UserApiService,
        private modalService: ModalService,
        private formDifferService: FormDifferService,
        private translateService: TranslateService,
    ) {
        super();

        effect(() => {
            const supplierId = this.supplierIdValue();
            if (!supplierId) {
                return;
            }

            const currentSupplier = this.suppliers?.find((supplier: Supplier) => supplier.id === supplierId);

            this.listingsService
                .getListingsFromCompanyName(currentSupplier?.companyName ?? this.userCompanyName())
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((listings) => {
                    this.listings.set(listings);

                    if (this.pendingSelectedListingIds) {
                        const selectedListingsArray = this.form.get('selectedListings') as FormArray<FormControl<string>>;
                        selectedListingsArray.clear();

                        const validListingIds = this.pendingSelectedListingIds.filter((listingId) =>
                            listings.some((listing: Listing) => listing.id === listingId),
                        );

                        validListingIds.forEach((listingId) => {
                            selectedListingsArray.push(new FormControl(listingId, { nonNullable: true }));
                        });

                        this.pendingSelectedListingIds = null;
                    }
                });
        });
    }

    public ngOnInit() {
        this.form.get('schedule.daysOfWeek')!.disable();
        this.form.get('schedule.dayOfMonth')!.disable();

        this.userApiService
            .getLoginInformationContinuously()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((loginInfo) => {
                this.currentUserId.set(loginInfo.id);
                this.isShijiUser.set(loginInfo.isShiji);

                if (!loginInfo.isShiji) {
                    const supplierUserDetails = loginInfo as CurrentSupplierUserInterface;
                    this.form.get('supplierId')!.setValue(supplierUserDetails.supplierId);
                    this.userCompanyName.set(supplierUserDetails.companyName);

                    if (this.data?.id) {
                        this.getScheduledSnapshotById(this.data.id);
                    }
                } else {
                    this.suppliersService
                        .getAllSuppliers()
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((suppliers: Supplier[]) => {
                            this.suppliers = suppliers;

                            if (this.data?.id) {
                                this.getScheduledSnapshotById(this.data.id);
                            }
                        });
                }
            });
    }

    getScheduledSnapshotById(scheduledSnapshotId: string) {
        this.scheduledChannelSnapshotsService
            .getScheduledChannelSnapshotById(scheduledSnapshotId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((scheduledChannelSnapshot) => {
                this.currentSnapshotEntityTracking.set(scheduledChannelSnapshot);
                this.patchFormFromModel(scheduledChannelSnapshot);
                this.formDifferService.registerForm(this.form);
                this.formDifferService.createDataSnapshot();
            });
    }

    patchFormFromModel(data: ScheduledChannelSnapshot): void {
        this.form.patchValue(
            {
                supplierId: data.supplierId,
                enabled: data.enabled,
                distributionChannels: data.channelIds,
                schedule: {
                    frequency: data.schedule.frequency,
                    time: data.schedule.time,
                    daysOfWeek: data.schedule.daysOfWeek ?? {
                        monday: false,
                        tuesday: false,
                        wednesday: false,
                        thursday: false,
                        friday: false,
                        saturday: false,
                        sunday: false,
                    },
                    dayOfMonth: data.schedule.dayOfMonth,
                },
                listingSelectionOptionTypeValue: data.listingSelectionOptionTypeValue,
            },
            { emitEvent: true },
        );

        if (data.selectedListings) {
            data.selectedListings.forEach((value) => {
                this.form.controls.selectedListings.push(new FormControl(value, { nonNullable: true }));
            });
        }

        if (data.selectedListings && data.selectedListings.length > 0) {
            this.pendingSelectedListingIds = data.selectedListings;
        }

        this.onFrequencyChange();
    }

    public onFrequencyChange() {
        const frequency = this.form.get('schedule.frequency')!.value as ScheduledChannelSnapshotFrequency;
        const fieldMap = frequencyFormFieldStatusMap[frequency];
        const fieldKeys = Object.keys(fieldMap) as (keyof typeof fieldMap)[];

        fieldKeys.forEach((key) => {
            const controlPath = `schedule.${key}`;
            const formControl = this.form.get(controlPath)!;

            if (fieldMap[key] === 'disable') {
                formControl.disable();
                formControl.reset();
                formControl.markAsUntouched();
            } else {
                formControl.enable();
            }
        });
    }

    public onSelectSelectedListings($event: string) {
        if (!$event || typeof $event !== 'string') {
            return;
        }

        this.form.get('tempListing')!.setValue(null);

        if ($event === '-1') {
            this.selectedListingsIds.clear();
            return;
        }

        const control = new FormControl<string>($event, { nonNullable: true });

        this.selectedListingsIds.push(control);
    }

    public closeModal() {
        this.closeModalInstance();
    }

    public saveScheduledChannelSnapshot() {
        updateTreeValidity(this.form);
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        if (this.data?.id && (this.form.pristine || !this.formDifferService.hasDataChanged())) {
            this.closeModalInstance(false);
            return;
        }

        // If we are editing an existing snapshot config,
        // a confirmation modal should be displayed for the user when making changes
        // only if the user is supplier (not shiji) and the snapshot config was not created / last modified by him.
        const createdByUserId = this.currentSnapshotEntityTracking()?.entityTracking?.createdById;
        const modifiedByUserId = this.currentSnapshotEntityTracking()?.entityTracking?.modifiedById;
        if (!this.isShijiUser() && this.data?.id && createdByUserId && this.currentUserId() !== (modifiedByUserId ?? createdByUserId)) {
            this.modalService
                .openSimpleModal(this.translateService.instant('dashboard.scheduledChannelSnapshots.save.confirmationModal.body'))
                .onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
                .subscribe(() => {
                    this.performSave();
                });
        } else {
            this.performSave();
        }
    }

    performSave() {
        const saveModel = this.getSaveModelFromForm();
        this.scheduledChannelSnapshotsService
            .saveScheduledChannelSnapshot(saveModel, this.data?.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toastService.showToast('dashboard.scheduledChannelSnapshots.toast.successfullySaved', 'success');
                this.closeModalInstance(true);
            });
    }

    getSaveModelFromForm(): Partial<ScheduledChannelSnapshot> {
        const formValue = this.form.getRawValue();

        return {
            supplierId: formValue.supplierId,
            enabled: formValue.enabled,
            channelIds: formValue.distributionChannels,
            schedule: {
                frequency: formValue.schedule.frequency,
                time: formValue.schedule.time,
                daysOfWeek: formValue.schedule.daysOfWeek,
                dayOfMonth: formValue.schedule.dayOfMonth,
            },
            listingSelectionOptionTypeValue: formValue.listingSelectionOptionTypeValue,
            selectedListings: formValue.selectedListings.length > 0 ? formValue.selectedListings : [],
        };
    }

    private generateExcludedListingsTableColumns(): DataGridColumnDefinition[] {
        return [
            {
                headerLabel: 'listings.table.shijiId',
                dataKey: 'shijiId',
                customCellRendererData: {
                    defaultValueIfNull: 'Shiji ID',
                },
            },
            {
                headerLabel: 'listings.general.listingName.label',
                dataKey: 'name',
            },
            {
                headerLabel: '',
                customCellRenderer: DefaultDataGridActionCellRendererComponent,
                customCellRendererData: [
                    {
                        label: 'generalKeywords.remove',
                        icon: 'trash',
                        clickHandler: this.removeListingMapping.bind(this),
                    },
                ],
            },
        ];
    }

    onSupplierChange() {
        this.selectedListingsIds.clear();
    }

    removeListingMapping(data: any): void {
        const index = this.selectedListingsIds.controls.findIndex((control) => control.value === data.id);
        this.selectedListingsIds.removeAt(index);

        this.form.markAsDirty();
    }

    onListingsSelectionModeChange(data: any) {
        this.form.get('listingSelectionOptionTypeValue')!.setValue(data);

        this.form.markAsDirty();
    }
}
